import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Image,
  Text,
  Container,
  Flex,
  VStack,
  Stack,
  Spacer,
  SimpleGrid,
} from '@chakra-ui/react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { BtNavigate } from '../../components/BtNavigate';
import { BookScreen } from './bookScreen';
import { RootState, store } from '../../app/store';
import { images } from '../../support/imageSupport';
import BtButton from '../../components/BtButton';
import BtInput from '../../components/BtInput';
import BtGroup from '../../components/BtGroup';
import BtListView from '../../components/BtListView';
import BtFailed from '../../components/BtFailed';
import { LandingScreen } from '../landing/landingScreen';
import { LANDING_SCREEN_PATH } from '../../support/routeConstants';
import BtLoading from '../../components/BtLoading';
import BtHelmet from '../../components/BtHelmet';
import BtSpinner from '../../components/BtSpinner';
import { useSelector } from 'react-redux';

export const Book: React.FC = () => {
  const viewModel = useAppSelector((state) => state.book.viewModel);
  const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [load, setLoading] = useState(false);
  const [state, setState] = useState({
    email: viewModel.state.email,
  });

  useEffect(() => {
    (async () => {
      dispatch(BookScreen.start(viewModel, logoUrl));
    })();
  }, [store.getState().appState.state]);

  useEffect(() => {
    document.querySelector('body')?.setAttribute('id', 'body');

    if (BookScreen.isNone(viewModel)) {
      dispatch(BookScreen.start(viewModel, logoUrl));
    } else if (BookScreen.isLoading(viewModel)) {
      (async () => {
        dispatch(await BookScreen.load(viewModel, logoUrl));
      })();
    }

    if (!store.getState().appState.state.sessionActive && BookScreen.isFailed(viewModel)) {
      handleNavigate(LANDING_SCREEN_PATH);
    }
  }, [viewModel]);

  const handleOnChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const target = event.target;
    const value = target.value;
    setState({ ...state, [target.name]: value });
    dispatch(await BookScreen.uppdateState(viewModel, event, logoUrl));
  };

  const handleNavigate = async (path: string) => {
    if (path.length === 0) {
      navigate(-1);
    } else {
      dispatch(await LandingScreen.reset(logoUrl));
      navigate(path);
    }
  };

  const handleBook = async () => {
    setLoading(true);
    dispatch(await BookScreen.book(viewModel, logoUrl));
    setLoading(false);
  };

  const handleInterrupt = () => {
    BookScreen.cancelFetch();
    setLoading(false);
  };

  if (BookScreen.isLoading(viewModel)) {
    return <BtLoading {...viewModel.loading} />;
  } else if (BookScreen.isReady(viewModel)) {
    return (
      <>
        <BtHelmet title={viewModel.metaTitle} />
        <BtSpinner
          {...viewModel.spinner}
          show={load}
          visible={false}
          onClick={() => handleInterrupt()}
        />
        <Container padding={0} size='xl'>
          <BtNavigate {...viewModel.navigation} onClick={handleNavigate} />
          <Container padding={0} maxW='container.md'>
            <Flex pb={200}>
              <VStack w='full' h='full' p={10}>
                <Heading as='h1' variant='h1' pb={5}>
                  {viewModel.greeting}
                </Heading>
                <Box w='100%' p={4} bg='black' borderRadius='lg' boxShadow='brand'>
                  <VStack spacing='1' w='100%' alignItems='start'>
                    {viewModel.vechicleItems !== null && (
                      <BtGroup items={viewModel.vechicleItems} />
                    )}
                    <Stack
                      w='full'
                      spacing={3}
                      direction={['column', 'column']}
                      p={5}
                      alignItems='start'
                    >
                      <Stack w='full' spacing={30} direction={['column', 'row']}>
                        <BtListView {...viewModel.date} />
                        <BtListView {...viewModel.time} />
                      </Stack>
                      <BtListView {...viewModel.from} />
                      <Image src={images(viewModel.arrowIcon)} alt={viewModel.arrowIcon} />
                      <BtListView {...viewModel.to} />
                      <BtListView {...viewModel.price} />
                    </Stack>
                    <Text pl={5}>{viewModel.yourInformation}</Text>
                    {viewModel.customerItems !== null && (
                      <BtGroup items={viewModel.customerItems} />
                    )}
                    <SimpleGrid
                      w='full'
                      columns={[1, 2]}
                      pl={5}
                      pr={5}
                      pt={5}
                      spacing={[2, 8]}
                      direction={['row', 'column']}
                    >
                      <BtInput {...viewModel.phone} onChange={handleOnChange} />
                      <BtInput {...viewModel.email} value={state.email} onChange={handleOnChange} />
                      <BtInput {...viewModel.firstName} onChange={handleOnChange} />
                      <BtInput {...viewModel.lastName} onChange={handleOnChange} />
                    </SimpleGrid>
                    <Box pl={5} pr={5} w='100%'>
                      <Text pt={5}>{viewModel.payment}</Text>
                    </Box>
                  </VStack>
                  <Stack p='5' spacing={2} direction={['column', 'column']}>
                    <BtButton
                      variant='primary'
                      size='lg'
                      w='full'
                      {...viewModel.complete}
                      action={() => handleBook()}
                    />
                    <Spacer />
                    <BtButton
                      variant='link'
                      size='lg'
                      w='full'
                      {...viewModel.change}
                      action={() => handleNavigate('')}
                    />
                  </Stack>
                </Box>
              </VStack>
            </Flex>
          </Container>
        </Container>
      </>
    );
  } else if (BookScreen.isFinish(viewModel)) {
    return (
      <Container padding={0} size='xl'>
        <BtNavigate {...viewModel.navigation} onClick={handleNavigate} />
        <Container padding={0} maxW='container.md'>
          <Flex pb={200}>
            <VStack w='full' h='full' p={10}>
              <Image src={images(viewModel.image)} />
              <Heading as='h1' variant='h1' textAlign='center' pb={5}>
                {viewModel.title}
              </Heading>
              <VStack spacing='5' w='100%'>
                <Stack
                  w='full'
                  spacing={5}
                  direction={['column', 'column']}
                  p={5}
                  alignItems='center'
                >
                  <Text align='center'>{viewModel.body}</Text>
                  <Text align='center'>{viewModel.bookingId}</Text>
                  <Box
                    w='100%'
                    p={4}
                    bg='black'
                    borderRadius='lg'
                    border='1px solid rgba(255, 212, 163, 0.2)'
                  >
                    <Text as='h3' fontSize='3xl' align='center'>
                      {viewModel.id}
                    </Text>
                  </Box>
                </Stack>
              </VStack>
              <Box w='100%' p={5}>
                <BtButton
                  variant='primary'
                  size='lg'
                  w='full'
                  {...viewModel.ok}
                  action={() => handleNavigate(LANDING_SCREEN_PATH)}
                />
              </Box>
            </VStack>
          </Flex>
        </Container>
      </Container>
    );
  } else if (BookScreen.isFailed(viewModel)) {
    return <BtFailed {...viewModel.error} />;
  } else {
    return <BtLoading {...viewModel.loading} />;
  }
};
